import React, { useState, useEffect } from 'react';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Upload, Trash2, Plus, Edit3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

interface Slide {
  id: number;
  title: string;
  description: string;
  image_url: string;
  order_index: number;
  language: string;
}

const SystemSlidesManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const [slides, setSlides] = useState<Slide[]>([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState<Slide | null>(null);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    language: 'en',
    order_index: 0,
    image: null as File | null,
  });
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    language: 'en',
    order_index: 0,
  });

  // Fetch slides
  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    try {
      const response = await api.get('/slides/');
      setSlides(response.data);
    } catch (error) {
      console.error('Failed to fetch slides:', error);
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const formData = new FormData();
    if (uploadForm.image) {
      formData.append('image', uploadForm.image);
    }
    formData.append('title', uploadForm.title);
    formData.append('description', uploadForm.description);
    formData.append('language', uploadForm.language);
    formData.append('order_index', uploadForm.order_index.toString());

    try {
      await api.post('/slides/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setIsUploadModalOpen(false);
      setUploadForm({ title: '', description: '', language: 'en', order_index: 0, image: null });
      fetchSlides();
    } catch (error) {
      console.error('Failed to upload slide:', error);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentSlide) return;

    try {
      await api.put(`/slides/${currentSlide.id}`, editForm);
      setIsEditModalOpen(false);
      fetchSlides();
    } catch (error) {
      console.error('Failed to update slide:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(t('slides.confirmDelete', 'Are you sure you want to delete this slide?'))) {
      try {
        await api.delete(`/slides/${id}`);
        fetchSlides();
      } catch (error) {
        console.error('Failed to delete slide:', error);
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploadForm({ ...uploadForm, image: e.target.files[0] });
    }
  };

  const handleEditClick = (slide: Slide) => {
    setCurrentSlide(slide);
    setEditForm({
      title: slide.title,
      description: slide.description || '',
      language: slide.language,
      order_index: slide.order_index,
    });
    setIsEditModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">{t('slides.title', 'Slides Management')}</h1>
          <p className="text-gray-600 mt-2">{t('slides.description', 'Manage slides displayed on the login page')}</p>
        </div>
        <div>
          <Button onClick={() => setIsUploadModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('slides.addSlide', 'Add Slide')}
          </Button>
          <Dialog open={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{t('slides.uploadTitle', 'Upload New Slide')}</DialogTitle>
                <DialogDescription>
                  {t('slides.uploadDescription', 'Upload an image that will be displayed on the login page')}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUpload} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">{t('slides.titleLabel', 'Title')}</Label>
                  <Input
                    id="title"
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">{t('slides.descriptionLabel', 'Description')}</Label>
                  <Input
                    id="description"
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="language">{t('slides.languageLabel', 'Language')}</Label>
                    <select
                      id="language"
                      value={uploadForm.language}
                      onChange={(e) => setUploadForm({ ...uploadForm, language: e.target.value })}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="en">English</option>
                      <option value="fr">French</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="order">{t('slides.orderLabel', 'Order')}</Label>
                    <Input
                      id="order"
                      type="number"
                      value={uploadForm.order_index}
                      onChange={(e) => setUploadForm({ ...uploadForm, order_index: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="image">{t('slides.imageLabel', 'Image File')}</Label>
                  <div className="flex items-center space-x-2">
                    <Input
                      id="image"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      required
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsUploadModalOpen(false)}>
                    {t('common.cancel', 'Cancel')}
                  </Button>
                  <Button type="submit">
                    <Upload className="mr-2 h-4 w-4" />
                    {t('slides.uploadButton', 'Upload')}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('slides.slidesList', 'Slides List')}</CardTitle>
          <CardDescription>
            {t('slides.slidesListDescription', 'All slides currently available for the login page')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('slides.table.image', 'Image')}</TableHead>
                <TableHead>{t('slides.table.title', 'Title')}</TableHead>
                <TableHead>{t('slides.table.description', 'Description')}</TableHead>
                <TableHead>{t('slides.table.language', 'Language')}</TableHead>
                <TableHead>{t('slides.table.order', 'Order')}</TableHead>
                <TableHead className="text-right">{t('slides.table.actions', 'Actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {slides.map((slide) => (
                <TableRow key={slide.id}>
                  <TableCell>
                    <img 
                      src={slide.image_url} 
                      alt={slide.title} 
                      className="w-16 h-16 object-cover rounded-md" 
                    />
                  </TableCell>
                  <TableCell className="font-medium">{slide.title}</TableCell>
                  <TableCell>{slide.description || '-'}</TableCell>
                  <TableCell>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                      {slide.language.toUpperCase()}
                    </span>
                  </TableCell>
                  <TableCell>{slide.order_index}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditClick(slide)}
                      >
                        <Edit3 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(slide.id)}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {slides.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {t('slides.noSlides', 'No slides uploaded yet. Add your first slide to get started.')}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Slide Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('slides.editTitle', 'Edit Slide')}</DialogTitle>
            <DialogDescription>
              {t('slides.editDescription', 'Update the slide information')}
            </DialogDescription>
          </DialogHeader>
          {currentSlide && (
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-title">{t('slides.titleLabel', 'Title')}</Label>
                <Input
                  id="edit-title"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-description">{t('slides.descriptionLabel', 'Description')}</Label>
                <Input
                  id="edit-description"
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-language">{t('slides.languageLabel', 'Language')}</Label>
                  <select
                    id="edit-language"
                    value={editForm.language}
                    onChange={(e) => setEditForm({ ...editForm, language: e.target.value })}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="en">English</option>
                    <option value="fr">French</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-order">{t('slides.orderLabel', 'Order')}</Label>
                  <Input
                    id="edit-order"
                    type="number"
                    value={editForm.order_index}
                    onChange={(e) => setEditForm({ ...editForm, order_index: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsEditModalOpen(false)}>
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button type="submit">
                  {t('common.save', 'Save')}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SystemSlidesManagementPage;